import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import stringify from 'fast-safe-stringify';
import clsx from 'clsx';
import { FormProvider, useFormContext, useFormState } from 'react-hook-form';
import { UseZodFormReturn, isActiveZodFormReturn } from '@repo/hooks/useZodForm';
import { ZodFormContext } from '@repo/hooks/useZodFormContext';
import Error, { type ErrorProps } from './Error';
import type { ZodObjecty } from '@repo/types/Types';

const safeStringify = (obj: unknown) =>
  stringify(obj, undefined, 2, {
    depthLimit: 4,
    edgesLimit: 4,
  })
    .split('\n')
    .join('\n  ');

const columns = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1sm:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-4',
};

export const fieldWidths = {
  full: 'sm:col-span-2 w-full',
  half: 'w-full',
};

export type FormProps<Schema extends ZodObjecty | undefined = ZodObjecty | undefined> =
  UseZodFormReturn<Schema> & {
    className?: string;
    variant?: 'vertical' | 'horizontal';
    gapless?: boolean;
    children?: React.ReactNode;
    column?: keyof typeof columns;
  };

export const Form = <Schema extends ZodObjecty | undefined = ZodObjecty | undefined>({
  className,
  children,
  variant,
  gapless,
  column = 2,
  ...rest
}: FormProps<Schema>) => {
  const form = rest as UseZodFormReturn<Schema>;
  if (!isActiveZodFormReturn(form)) return <></>;

  return (
    <ZodFormContext.Provider value={form as UseZodFormReturn}>
      <FormProvider {...(form as UseZodFormReturn<ZodObjecty>)}>
        <form
          className={clsx(
            'grid w-full',
            {
              'gap-6': !gapless,
              'flex-col max-w-md': !variant || variant === 'vertical',
              '!grid-cols-1 items-end': variant === 'horizontal',
            },
            columns[column],
            className
          )}
          onSubmit={(event) => form.formState?.onSubmit?.(event)}
        >
          {children}
        </form>
      </FormProvider>
    </ZodFormContext.Provider>
  );
};

export interface FormErrorProps extends Omit<ErrorProps, 'error'> {
  width?: keyof typeof fieldWidths;
}

export function FormError({ onClear, width = 'full', ...props }: FormErrorProps) {
  const form = useFormContext();
  const formState = useFormState();
  const error = formState.errors?.['root']?.['server'];
  const onActualClear = useMemo(
    () => () => {
      form.clearErrors('root.server');
      onClear?.();
    },
    [onClear, form]
  );
  return <Error width={width} {...props} error={error} onClear={onActualClear} />;
}
export interface FormSuccessProps {
  children?: ReactNode;
  autoClose?: number | true | false;
  width?: keyof typeof fieldWidths;
  fixed?: boolean;
  refreshPage?: boolean;
}
const DEFAULT_SUCCESS_AUTOCLOSE = 2000;
export function FormSuccess({
  children,
  autoClose,
  width = 'full',
  fixed = false,
  refreshPage = false,
}: FormSuccessProps) {
  const { submitCount, isSubmitted, isSubmitSuccessful } = useFormState();
  const [prevCount, setPrevCount] = useState(0);
  const [isSuccess, setIsSuccess] = useState(false);

  const navigate = useNavigate();
  useEffect(() => {
    if (submitCount === prevCount) return;
    if (!isSubmitted) return;
    setPrevCount(submitCount);
    setIsSuccess(isSubmitSuccessful);
  }, [prevCount, submitCount, isSubmitted, isSubmitSuccessful]);
  useEffect(() => {
    if (!isSuccess) return;
    if (autoClose === undefined || autoClose === true) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      autoClose = DEFAULT_SUCCESS_AUTOCLOSE;
    }
    if (autoClose === false || autoClose < 1) {
      return;
    }
    const timeout = setTimeout(() => setIsSuccess(false), autoClose);
    return () => {
      clearTimeout(timeout);
      if (refreshPage) {
        navigate(0);
      }
    };
  }, [isSuccess, autoClose]);
  if (!isSuccess) {
    return <></>;
  }
  return (
    <>
      <div
        className={clsx(
          fieldWidths[width],
          'p-2 text-green-800 bg-green-200 border border-green-800 rounded-lg',
          fixed ? 'fixed bottom-0 right-0 max-w-sm rounded-r-none rounded-bl-none' : ''
        )}
      >
        {children}
      </div>
    </>
  );
}
export function FormSubmitting({ children }: { children?: ReactNode }) {
  const formState = useFormState();
  if (!formState.isSubmitting) return;
  return <>{children}</>;
}

export function FormDirty({ children }: { children?: ReactNode }) {
  const formState = useFormState();
  if (!formState.isDirty) return;
  return <>{children}</>;
}

export function FormLoading({ children }: { children?: ReactNode }) {
  const formState = useFormState();
  if (!formState.isLoading) return;
  return <>{children}</>;
}

export function FormDebug() {
  const formState = useFormState();
  const form = useFormContext();
  const [side, setSide] = useState<'left' | 'right'>('right');
  const onDoubleClick = () => {
    setSide((input) => (input === 'left' ? 'right' : 'left'));
  };
  return (
    <>
      <pre
        onDoubleClick={onDoubleClick}
        className={clsx(
          'fixed w-1/2 bottom-0 z-20 max-h-[33%] p-2 overflow-scroll font-mono text-sm text-white bg-black rounded opacity-85',
          { 'right-0': side === 'right', 'left-0': side === 'left' }
        )}
      >
        {`formState = {
  dirtyFields: ${safeStringify(formState.dirtyFields)},
  disabled: ${formState.disabled},
  errors: {${Object.entries(formState.errors)
    .map(([key, value]) => `\n    ${key}: ${safeStringify(value)}`)
    .join(',\n')}
  },
  isDirty: ${formState.isDirty},
  isLoading: ${formState.isLoading},
  isSubmitSuccessful: ${formState.isSubmitSuccessful},
  isSubmitted: ${formState.isSubmitted},
  isSubmitting: ${formState.isSubmitting},
  isValid: ${formState.isValid},
  isValidating: ${formState.isValidating},
  submitCount: ${formState.submitCount},
  defaultValues: ${safeStringify(formState.defaultValues)},
  touchedFields: ${safeStringify(formState.touchedFields)},
  values: ${safeStringify(form.getValues())}
}`}
      </pre>
    </>
  );
}

Form.Error = FormError;
Form.Success = FormSuccess;
Form.Submitting = FormSubmitting;
Form.Loading = FormLoading;
Form.Debug = FormDebug;
